// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file <INSERT_FILE_DESCRIPTION_HERE>
 * @author James Reid
 */

// @ts-check

// @@imports-dependencies
import {
    toKebabCase,
    EpdmCalculator,
    Deck,
    PRODUCT_DISPLAY_CATEGORIES,
    SeamDirection
} from "../../../../../package/index.js"

// @@imports-package
import { deckEditor } from "../../../layouts/index.js"

// @@imports-utils
import {
    cloneTemplate,
    getDeckImageUrl,
    renderLucideIcons
} from "../../../utils/index.js"

// @@imports-types
import { App } from "../../../app/index.js"
import { SteppedContainer } from "../../containers/SteppedContainer.js"
import { CollapsibleContainer } from "../../containers/CollapsibleContainer.js"
import { ListSelect } from "../../elements/ListSelect.js"

// @@body
class QuoteOverviewCard extends HTMLElement {
    constructor() {
        super()
    }

    async connectedCallback() {
        // 
        const card = cloneTemplate("quote-overview")

        // 
        const deck = /** @type {Deck} */ (App.state.deck.client);

        // 
        /** @type {HTMLSpanElement} */ (card.querySelector("span.quote-id"))
            .innerText = this.#calculator.id;
        /** @type {HTMLSpanElement} */ (card.querySelector("span.substrate"))
            .innerText = this.#calculator.config.substrate === "osb" ? "OSB"
                : this.#calculator.config.substrate === "grp" ? "GRP"
                : this.#calculator.config.substrate === "metal" ? "Metal"
                : this.#calculator.config.substrate === "felt" ? "Felt"
                : "Unknown";
        /** @type {HTMLSpanElement} */ (card.querySelector("span.perimeter"))
            .innerText = `${(deck.perimeter / 100).toFixed(2)}`;
        /** @type {HTMLSpanElement} */ (card.querySelector("span.area"))
            .innerText = `${(deck.area / 10000).toFixed(2)}`;

        /** @type {HTMLButtonElement} */ (card
            .querySelector("button.tertiary[data-role='modify']"))
            .addEventListener("click", () => {
                App.container.renderStep(deckEditor)
            });

        // Generate deck image with numbered edges and prepend to overview
        // container adjacent to the deck edges table.
        const deckImage = /** @type {HTMLImageElement} */ (card
            .querySelector("div#deck-overview img"))
        const dataUrl = await getDeckImageUrl(deck, {
            rotation: 0,
            skew: 0,
            shadowWidth: 0,
            renderEdgeLabels: true,
            forceRender: true
        })
        deckImage.src = dataUrl

        // 
        for (const [index, edge] of deck.edges.array.entries()) {
            const row = document.createElement("tr")
            row.classList.add("__row")

            const spacer = document.createElement("td")
            spacer.classList.add("__cell", "--spacer")

            const number = document.createElement("td")
            number.classList.add("__cell")
            number.innerText = `${index + 1}`

            const edgeType = deck.getEdgeType(edge)
            const type = document.createElement("td")
            type.classList.add("__cell")
            type.innerText = toKebabCase(edgeType, true).replaceAll("-", " ")

            const length = document.createElement("td")
            length.classList.add("__cell")
            length.innerText = `${(edge.magnitude / 100).toFixed(2)}m`

            row.append(spacer, number, type, length)
            card.querySelector("tbody")?.append(row)
        }

        // 
        this.append(card)
        renderLucideIcons()

        // 
        this.renderState()
        App.addStateListener(() => { this.renderState() })
    }

    /**
     * Remove app state listener.
     */
    disconnectedCallback() {
        App.removeStateListener(this.renderState)
    }

    async renderState() {
        const lineItemRows = []
        for (const lineItem of this.#calculator.bom.lineItems) {
            if (!lineItem.quantity) { continue }

            const spacer = document.createElement("td")
            spacer.classList.add("__cell", "--spacer")

            const name = document.createElement("td")
            name.innerText = lineItem.product.name
            name.classList.add("__cell")

            const quantity = document.createElement("td")
            quantity.innerText = `${lineItem.quantity}`
            quantity.classList.add("__cell")

            const row = document.createElement("tr")
            row.append(spacer, name, quantity)
            row.classList.add("__row")
            lineItemRows.push(row)
        }

        /** @type {HTMLTableSectionElement} */ (this
            .querySelector("[data-accessor='line-items']"))
            .replaceChildren(...lineItemRows.reverse())
    }

    get #calculator() {
        return /** @type {EpdmCalculator} */ (App.state.quote.calculator)
    }

    static get customName() {
        return "quote-overview-card"
    }
}

// @@exports
export { QuoteOverviewCard }
